import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { Ban, Ellipsis } from "lucide-react";
import { bookingCancel } from "@shared/schemas/bookings";
import { bookingHasEffectiveHold } from "@shared/validation/holdExpiry";
import { evaluation, parseInput, type ValidationIssue } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { useFormValidation } from "@web/lib/formValidation";
import { bookingTitle, fmtRange, fullName, statusLabel, statusTone } from "@web/lib/format";
import { useNavAction } from "@web/lib/navAction";
import { api, bookingQuery, type BookingDetail as Booking } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { recordValidationQuery } from "@web/lib/validation";
import { UnsavedChanges, ValidationSummary, focusValidationField } from "@web/ui";
import { BottomSheet, Button, Card, EmptyState, Inline, MoneyText, Page, Row, RowGroup, SegmentedControl, Skeleton, Stack, StatusChip, Text, TextArea, RetainedPanel, useToast } from "@web/ui";
import { ActivityTab } from "./booking/ActivityTab";
import { EventTab } from "./booking/EventTab";
import { PaymentsTab } from "./booking/PaymentsTab";
import { PricingTab } from "./booking/PricingTab";
import { ContractTab } from "./booking/ContractTab";
import { FilesTab } from "./booking/FilesTab";
import { InsuranceTab } from "./booking/InsuranceTab";
import { ReviewSheet } from "./booking/ReviewSheet";
import { ContactSheet } from "./sheets/ContactSheet";

const TABS = [
  { key: "event", label: "Event" },
  { key: "pricing", label: "Pricing" },
  { key: "payments", label: "Payments" },
  { key: "contract", label: "Contract" },
  { key: "insurance", label: "Insurance & vendors" },
  { key: "files", label: "Files" },
  { key: "activity", label: "Activity" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

export const ACTIVE = ["pending", "contract_sent", "signed", "booked"];

/** Booking detail (SPEC §6.1 item 8): header · Event / Pricing / Payments / Activity. */
export function BookingDetail() {
  const { id } = useParams({ strict: false }) as { id: string };
  const me = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const q = useQuery(bookingQuery(id));
  const validation = useQuery(recordValidationQuery("booking", id));
  const location = useLocation();
  const [tab, setTab] = useState<TabKey>(() => TABS.find(item => item.key === location.hash)?.key ?? "event");
  useEffect(() => { const requested = TABS.find(item => item.key === location.hash); if (requested) setTab(requested.key); }, [location.hash, id]);
  const [contactId, setContactId] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const [cancelFailure, setCancelFailure] = useState<string | null>(null);
  const parsedCancel = parseInput(bookingCancel, { reason }, { type: "booking", id }, "booking.save");
  const cancelValidation = useFormValidation(evaluation(parsedCancel.issues, false));
  const { busy } = cancelValidation;
  // The router can reuse this component for another booking. An explicitly
  // discarded cancellation draft must never become that record's reason.
  useEffect(() => { setReason(""); setCancelFailure(null); setCancelling(false); setMore(false); cancelValidation.clear(); }, [id]);
  const b = q.data;
  const locked = Boolean(b && (b.status === "completed" || b.legacyRef !== null) && !me.companyAdmin);
  const readOnly = locked || b?.status === "cancelled";
  function openIssue(issue: ValidationIssue) {
    if (issue.entity.type === "contact" && issue.entity.id) { setContactId(issue.entity.id); return; }
    if (issue.fields.some(field => /review|approved|checklist/i.test(field))) { setReviewing(true); return; }
    const next = issue.fields.some(field => /^(lines|packageId|total|subtotal|deposit|damage)/.test(field)) ? "pricing" : issue.entity.type === "payment" || issue.fields.some(field => /^(paid|balance|payment)/.test(field)) ? "payments" : "event";
    setTab(next); requestAnimationFrame(() => focusValidationField(issue));
  }
  useNavAction(b && !readOnly && ACTIVE.includes(b.status) ? { label: "More", icon: <Ellipsis aria-hidden size={24} />, onClick: () => setMore(true) } : null);

  async function cancel() {
    if (!parsedCancel.success || !cancelValidation.begin("booking.save")) return;
    setCancelFailure(null);
    try {
      await api.cancelBooking(id, parsedCancel.data.reason);
      toast.success("Booking cancelled");
      setReason("");
      cancelValidation.clear();
      setCancelling(false);
      setMore(false);
    } catch (e) {
      cancelValidation.reject(e);
      setCancelFailure(errorMessage(e));
    } finally {
      cancelValidation.finish();
    }
  }

  if (q.isError)
    return (
      <Page>
        <EmptyState title="Booking not found" body={errorMessage(q.error)} action={<Button fullWidth={false} variant="secondary" onClick={() => navigate({ to: "/bookings" })}>Back to bookings</Button>} />
      </Page>
    );
  if (!b)
    return (
      <Page>
        <Skeleton variant="cards" count={3} />
      </Page>
    );

  return (
    <Page>
      <UnsavedChanges dirty={Boolean(reason)} pending={busy} />
      <Stack gap={4}>
        <Header b={b} checkedAt={validation.data?.checkedAt} onContact={setContactId} />
        <Inline justify="between"><StatusChip tone={b.reviewStatus === "approved" ? "success" : "warn"}>{b.reviewStatus === "approved" ? "Approved" : b.reviewStatus === "reviewed" ? "Awaiting approval" : "Needs review"}</StatusChip><Button variant="secondary" fullWidth={false} onClick={() => setReviewing(true)}>Review booking</Button></Inline>
        {locked && (
          <Text variant="caption" tone="tertiary" align="center">
            Admins can edit migrated/completed bookings
          </Text>
        )}
        {b.status === "cancelled" && (
          <Text variant="caption" tone="danger" align="center">
            Cancelled{b.cancelReason ? ` — ${b.cancelReason}` : ""}
          </Text>
        )}
        <SegmentedControl items={TABS} value={tab} onChange={setTab} scrollable />
        {tab !== "event" && <ValidationSummary result={validation.data} saved loading={validation.isFetching} unavailable={validation.isError} onRetry={() => void validation.refetch()} onIssue={issue => { if (issue.entity.type === "contact" && issue.entity.id) setContactId(issue.entity.id); else { setTab("event"); requestAnimationFrame(() => focusValidationField(issue)); } }} />}
        <RetainedPanel active={tab === "event"} label="Event"><EventTab key={b.id} b={b} readOnly={readOnly} onContact={setContactId} onPricing={() => setTab("pricing")} /></RetainedPanel>
        <RetainedPanel active={tab === "pricing"} label="Pricing"><PricingTab key={b.id} b={b} readOnly={readOnly} /></RetainedPanel>
        {tab === "payments" && <PaymentsTab b={b} readOnly={b.status === "cancelled" || (b.status === "completed" && !me.companyAdmin)} />}
        <RetainedPanel active={tab === "contract"} label="Contract"><ContractTab key={b.id} booking={b} readOnly={readOnly} onIssue={openIssue} /></RetainedPanel>
        <RetainedPanel active={tab === "insurance"} label="Insurance and vendors"><InsuranceTab key={b.id} booking={b} readOnly={readOnly} /></RetainedPanel>
        <RetainedPanel active={tab === "files"} label="Files"><FilesTab key={b.id} bookingId={b.id} readOnly={readOnly} /></RetainedPanel>
        {tab === "activity" && <ActivityTab bookingId={b.id} />}
      </Stack>
      <ContactSheet contactId={contactId} onClose={() => setContactId(null)} />
      <ReviewSheet key={b.id} bookingId={b.id} open={reviewing} onClose={() => setReviewing(false)} onSection={section => { setReviewing(false); setTab(section); }} />
      <BottomSheet open={more} onClose={() => setMore(false)} title="Booking">
        <RowGroup>
          <Row icon={<Ban aria-hidden size={22} />} title="Cancel booking" subtitle="Releases the date; needs a reason" onClick={() => setCancelling(true)} />
        </RowGroup>
      </BottomSheet>
      <BottomSheet
        open={cancelling}
        onClose={() => cancelValidation.edit(() => setCancelling(false))}
        title="Cancel booking"
        footer={
          <Button variant="destructive" loading={busy} onClick={cancel}>
            Cancel booking
          </Button>
        }
      >
        <Stack gap={3}>
          <ValidationSummary result={cancelValidation.result} local unavailable={cancelValidation.unavailable} />
          {cancelFailure && <div role="alert"><Text tone="danger">{cancelFailure}</Text></div>}
          <TextArea label="Reason" placeholder="Why is this booking cancelled?" value={reason} onChange={(e) => cancelValidation.edit(() => { setReason(e.target.value); setCancelFailure(null); })} autoFocus {...cancelValidation.field("reason")} />
        </Stack>
      </BottomSheet>
    </Page>
  );
}

function Header({ b, checkedAt, onContact }: { b: Booking; checkedAt?: string; onContact: (id: string) => void }) {
  const client = bookingTitle({ ...b, clientName: fullName(b.contact) });
  const held = bookingHasEffectiveHold(b, checkedAt ?? b.expiryCheckedAt ?? new Date());
  const sub = `${b.venue?.name ?? ""} · ${fmtRange(b.eventDate, b.eventEndDate ?? b.eventDate)}${b.eventName ? ` · ${b.eventName}` : ""}`;
  return (
    <Card variant="record-header">
      <Row
        title={client}
        subtitle={sub}
        wrap
        trailing={<StatusChip tone={held ? "warn" : statusTone(b.status)}>{held ? "Hold" : statusLabel(b.status)}</StatusChip>}
        onClick={b.contactId ? () => onContact(b.contactId!) : undefined}
      />
      <Inline gap={4} justify="between" variant="record-figures">
        <Figure label="Total" cents={b.total} />
        <Figure label="Paid" cents={b.paidTotal} />
        <Figure label="Due" cents={b.balanceDue} tone={b.balanceDue > 0 ? "danger" : "default"} />
      </Inline>
    </Card>
  );
}

function Figure({ label, cents, tone }: { label: string; cents: number; tone?: "default" | "danger" }) {
  return (
    <Stack gap={0} align="center" className="flex-1">
      <Text variant="caption" tone="tertiary">
        {label}
      </Text>
      <MoneyText cents={cents} variant="headline" tone={tone} />
    </Stack>
  );
}
